import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import {
  UNLOCK_PRICE,
  emptyStore,
  isRoleUnlocked,
  loadEmployer,
  loadEmployerKind,
  unlockRole,
  type EmployerJob,
  type EmployerStore,
} from "@/components/site/employer";
import {
  createUnlockIntent,
  fetchEmployerWorkspace,
  getEmployerSession,
  unlockJobAccess,
} from "@/lib/employer-api";
import { getStripe, hasStripeKey } from "@/lib/stripe";

export const Route = createFileRoute("/checkout")({
  validateSearch: (search: Record<string, unknown>): { job?: string } =>
    typeof search["job"] === "string" && search["job"] ? { job: search["job"] as string } : {},
  head: () => {
    const t = "Checkout — unlock your applications | CLOSDD";
    const d = `Complete your one-off $${UNLOCK_PRICE} payment to unlock the candidates on this role: full names, CVs and contact details.`;
    return {
      meta: [
        { name: "robots", content: "noindex,nofollow" },
        { title: t },
        { name: "description", content: d },
        { property: "og:title", content: t },
        { property: "og:description", content: d },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  component: CheckoutPage,
});

const btn =
  "inline-flex items-center justify-center rounded-lg bg-purple-strong px-6 py-3.5 text-[13.5px] font-semibold text-white shadow-[0_10px_30px_-12px_rgba(82,0,128,0.6)] transition-colors hover:bg-purple-mid disabled:opacity-55";
const label = "text-[11px] font-semibold uppercase tracking-[0.16em] text-ink/45";
const inputClass =
  "mt-2 w-full rounded-xl border border-purple-strong/12 bg-white px-3.5 py-3 text-[15px] text-ink outline-none transition focus:border-purple-strong/50";

const included = [
  "Full candidate name and profile",
  "Direct email and phone contact",
  "Shortlist and manage every candidate",
];

function OrderSummary({ role }: { role: EmployerJob | undefined }) {
  return (
    <aside className="rounded-2xl border border-purple-strong/10 bg-white px-6 py-7">
      <p className={label}>Order summary</p>
      <div className="mt-4 flex items-baseline justify-between gap-3 border-b border-purple-strong/10 pb-4">
        <span className="text-[15px] font-semibold text-ink">
          {role ? role.title : "Candidate access"}
        </span>
        <span className="font-serif text-[20px] tracking-[-0.02em] text-ink">${UNLOCK_PRICE}</span>
      </div>
      <p className="mt-3 text-[13px] leading-[1.55] text-ink/50">
        Unlock the candidates on this role. This unlock applies to this role only. Each role is a
        separate ${UNLOCK_PRICE} unlock.
      </p>
      <ul className="mt-5 grid gap-3 text-[14px] leading-[1.5] text-ink/70">
        {included.map((t) => (
          <li key={t} className="flex gap-3">
            <span className="mt-[2px] grid h-5 w-5 shrink-0 place-items-center rounded-full bg-purple-strong/10 text-purple-strong">
              <svg
                viewBox="0 0 12 12"
                className="h-3 w-3"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M2.5 6.5 5 9l4.5-5.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span>{t}</span>
          </li>
        ))}
      </ul>
      <div className="mt-6 flex items-baseline justify-between gap-3 border-t border-purple-strong/10 pt-4">
        <span className="text-[13px] font-semibold uppercase tracking-[0.14em] text-ink/45">
          Total today
        </span>
        <span className="text-[17px] font-semibold text-ink">${UNLOCK_PRICE}</span>
      </div>
    </aside>
  );
}

/** The real Stripe form — only ever rendered inside an <Elements> provider, once a PaymentIntent's clientSecret exists. */
function StripeCheckoutForm({
  jobId,
  role,
  onUnlocked,
}: {
  jobId: string;
  role: EmployerJob | undefined;
  onUnlocked: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState("");

  async function pay(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!stripe || !elements || paying) return;
    setPaying(true);
    setPayError("");

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });
    if (error) {
      setPayError(error.message ?? "Payment failed. Try again.");
      setPaying(false);
      return;
    }
    if (!paymentIntent || paymentIntent.status !== "succeeded") {
      setPayError("Payment did not complete. Try again.");
      setPaying(false);
      return;
    }

    try {
      // The backend independently re-verifies this PaymentIntent with
      // Stripe before it flips access — see Api\Employer\Access::unlock().
      await unlockJobAccess(jobId, paymentIntent.id);
      onUnlocked();
    } catch (err) {
      setPayError(
        err instanceof Error
          ? err.message
          : "Payment succeeded but we couldn't unlock the role — contact info@closdd.com.",
      );
      setPaying(false);
    }
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_360px] lg:items-start">
      <form
        onSubmit={pay}
        className="rounded-2xl border border-purple-strong/10 bg-white px-7 py-8"
      >
        <p className={label}>Card payment</p>
        <div className="mt-4">
          <PaymentElement />
        </div>

        {payError ? (
          <p className="mt-4 text-center text-[13.5px] font-semibold text-tag-red">{payError}</p>
        ) : null}
        <button
          type="submit"
          disabled={!stripe || !elements || paying}
          className={`${btn} mt-8 w-full`}
        >
          {paying ? "Processing payment…" : `Pay $${UNLOCK_PRICE} and unlock the candidates`}
        </button>
        <p className="mt-3 text-center text-[12.5px] text-ink/40">
          Payments are processed securely by Stripe. Questions? info@closdd.com
        </p>
      </form>
      <OrderSummary role={role} />
    </div>
  );
}

/** Sample/local employer data has no real job to bill — this simulates the same unlock without touching Stripe. */
function DemoCheckoutForm({
  role,
  onUnlocked,
}: {
  role: EmployerJob | undefined;
  onUnlocked: () => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [paying, setPaying] = useState(false);
  const ready = name.trim().length > 1 && /.+@.+\..+/.test(email);

  async function pay(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!ready || paying) return;
    setPaying(true);
    await new Promise((r) => setTimeout(r, 700));
    onUnlocked();
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_360px] lg:items-start">
      <form
        onSubmit={pay}
        className="rounded-2xl border border-purple-strong/10 bg-white px-7 py-8"
      >
        <p className={label}>Your details</p>
        <p className="mt-2 text-[12.5px] text-ink/45">
          Demo account — this simulates the unlock without a real charge.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <span className="text-[13px] font-semibold text-ink/70">Full name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
          </div>
          <div>
            <span className="text-[13px] font-semibold text-ink/70">Email for the receipt</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
        <button type="submit" disabled={!ready || paying} className={`${btn} mt-8 w-full`}>
          {paying ? "Processing payment…" : `Pay $${UNLOCK_PRICE} and unlock the candidates`}
        </button>
      </form>
      <OrderSummary role={role} />
    </div>
  );
}

function CheckoutPage() {
  const navigate = useNavigate();
  const { job: jobId } = Route.useSearch();
  const [store, setStore] = useState<EmployerStore>(emptyStore);
  const [kind, setKind] = useState<"direct" | "sourced">("direct");
  // True once we know this browser holds a real (API-backed) employer session,
  // as opposed to the legacy localStorage "sourced" console.
  const [isRealEmployer, setIsRealEmployer] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [intentError, setIntentError] = useState("");

  useEffect(() => {
    (async () => {
      const user = await getEmployerSession();
      if (user) {
        setIsRealEmployer(true);
        setKind("direct");
        const s = await fetchEmployerWorkspace().catch(() => emptyStore);
        setStore(s);
        return;
      }
      const s = loadEmployer();
      setStore(s);
      setKind(loadEmployerKind());
    })();
  }, []);

  const consoleTo = kind === "sourced" ? "/employer-preview" : "/employer";
  const role = jobId ? store.jobs.find((j) => j.id === jobId) : undefined;
  const unlocked = jobId ? isRoleUnlocked(store, jobId) : false;

  // Real employers need a PaymentIntent from the backend before the card form can render.
  useEffect(() => {
    if (!isRealEmployer || !jobId || unlocked || !hasStripeKey) return;
    setIntentError("");
    createUnlockIntent(jobId)
      .then((res) => setClientSecret(res.clientSecret))
      .catch((err) =>
        setIntentError(err instanceof Error ? err.message : "Could not start checkout. Try again."),
      );
  }, [isRealEmployer, jobId, unlocked]);

  const goToThankYou = () => {
    void navigate({ to: "/checkout/thank-you", search: { to: consoleTo } });
  };

  return (
    <div className="min-h-screen bg-[#f6f8fc]">
      <header className="border-b border-white/10 bg-purple-strong">
        <div className="mx-auto flex w-full max-w-[1120px] items-center justify-between gap-6 px-5 py-3.5">
          <Link to="/" className="font-serif text-[16px] font-bold tracking-[-0.01em] text-white">
            CLOSDD
          </Link>
          <Link
            to={consoleTo}
            className="text-[13px] font-semibold text-white/70 transition hover:text-white"
          >
            Back to your candidates
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[980px] px-5 pb-20 pt-14">
        <div className="mb-10 text-center">
          <p className={label}>Secure checkout</p>
          <h1 className="mx-auto mt-3 max-w-[560px] font-serif text-[25px] leading-[1.08] tracking-[-0.025em] text-ink sm:text-[30px]">
            Unlock the candidates on this role
          </h1>
          <p className="mx-auto mt-4 max-w-[520px] text-[15px] leading-[1.6] text-ink/55">
            One payment of ${UNLOCK_PRICE} for this role. No subscription, no fee per hire.
          </p>
        </div>

        {unlocked ? (
          <div className="mx-auto max-w-[560px] rounded-2xl border border-purple-strong/10 bg-white px-7 py-8 text-center">
            <p className="text-[16px] font-semibold text-ink">
              {role ? `${role.title} is already unlocked` : "You already have full access"}
            </p>
            <Link to={consoleTo} className={`${btn} mt-5`}>
              Go to your candidates
            </Link>
          </div>
        ) : !isRealEmployer ? (
          <DemoCheckoutForm
            role={role}
            onUnlocked={() => {
              if (jobId) setStore(unlockRole(jobId));
              goToThankYou();
            }}
          />
        ) : !jobId ? (
          <p className="mx-auto max-w-[480px] text-center text-[14px] text-ink/55">
            No role selected. Go back to your dashboard and choose a role to unlock.
          </p>
        ) : !hasStripeKey ? (
          <div className="mx-auto max-w-[560px] rounded-2xl border border-tag-amber/25 bg-tag-amber-soft px-7 py-8 text-center">
            <p className="text-[14px] font-semibold text-ink">Payments aren't configured yet.</p>
            <p className="mt-2 text-[13.5px] text-ink/60">
              Contact info@closdd.com to unlock this role in the meantime.
            </p>
          </div>
        ) : intentError ? (
          <p className="mx-auto max-w-[480px] text-center text-[14px] font-semibold text-tag-red">
            {intentError}
          </p>
        ) : clientSecret ? (
          <Elements
            stripe={getStripe()}
            options={{
              clientSecret,
              appearance: { theme: "stripe", variables: { colorPrimary: "#10306b" } },
            }}
          >
            <StripeCheckoutForm
              jobId={jobId}
              role={role}
              onUnlocked={goToThankYou}
            />
          </Elements>
        ) : (
          <p className="text-center text-[13.5px] text-ink/45">Loading checkout…</p>
        )}
      </main>
    </div>
  );
}
